import { ReportingConfig } from '@opengov/app-config-reporting';
import { getEntitySubdomain, getNumericalEntityId } from '@opengov/app-core/entity/selectors';
import { getLocation } from '@opengov/compose-app-router/selectors';
import { IRouteMatchResult } from '@opengov/compose-app-router/types';
import { createSelector } from '@opengov/compose-app/reselect';
import { isMultiHierarchyAccountSummary, resolveTransparencyPublicApiHost } from '@opengov/reporting-types';

import { makeReportExplorerPath, makeReportSavedViewPath } from '../../../../module/routes/paths';
import { useRouterStore } from '../../../../module/routes/routerStore';
import { selectCurrentReportConfiguration } from '../../reportConfiguration/selectors';
import { selectCurrentSavedView, selectSavedViews } from '../../savedViews/selectors';
import { reportSelector } from '../state/reportState';
import { selectPathname } from './pathnameSelectors';

export const selectReactRouterRouteName = () => useRouterStore.getState().currentRouteName;

export const selectReports = createSelector(reportSelector, (state) => state.reports);

export const selectFilters = createSelector(reportSelector, (state) => state.filters);

export const selectPagination = createSelector(reportSelector, (state) => state.pagination);

export const selectDatasetLensesForReports = createSelector(reportSelector, (state) => state.dataLenses);

export const selectLoadingDatasetLensForReports = createSelector(
  reportSelector,
  (state) => state.loadingDataLenses.active
);

export const selectCurrentReportId = createSelector(
  getLocation,
  selectPathname,
  (location: Immutable.Record<IRouteMatchResult> & Readonly<IRouteMatchResult>, pathname: string) => {
    if (location.params?.reportId) return location.params.reportId;
    /*
     * In some cases we need to parse pathname
     * in other cases pathname doesn't have reportId yet so we need useRouterStore.getState().params.reportId
     */
    const reportIdFromPathname = pathname.includes('accountSummary') ? pathname.split('/')[4] : pathname.split('/')[3];
    return reportIdFromPathname || useRouterStore.getState().params.reportId;
  }
);

export const selectCurrentReportConfigurationId = createSelector(
  selectCurrentReportConfiguration,
  getLocation,
  selectPathname,
  (
    reportConfiguration,
    location: Immutable.Record<IRouteMatchResult> & Readonly<IRouteMatchResult>,
    pathname: string
  ) => {
    if (reportConfiguration?.id || location.params?.reportConfigurationId)
      return reportConfiguration?.id || location.params?.reportConfigurationId;
    const reportConfigurationId = pathname.includes('accountSummary') ? pathname.split('/')[5] : pathname.split('/')[4];
    return reportConfigurationId || useRouterStore.getState().params.reportConfigurationId || null;
  }
);

export const selectReportsSortedByTitle = createSelector(selectReports, (reports) =>
  reports.sortBy((report) => report.title)
);

export const selectIsReportsLoading = createSelector(reportSelector, (state) => state.loadingReports.active);

export const selectReportsLoadingError = createSelector(reportSelector, (state) => state.loadingReports.error);

export const selectReportLoadingStatus = createSelector(reportSelector, (state) => state.loadingReport);

export const selectReportsForCurrentEntity = createSelector(
  selectReports,
  getNumericalEntityId,
  (reports, entityId) => {
    return reports.filter((r) => r.entityId === entityId);
  }
);

export const selectCurrentReport = createSelector(selectReports, selectCurrentReportId, (reports, reportId) =>
  reports.find((report) => report.id === reportId)
);

export const selectReportHasLens = createSelector(selectCurrentReport, (report) => report && !!report.lensId);

export const selectReportHasView = createSelector(selectCurrentReport, (report) => report && !!report.viewId);

export const selectCurrentReportTitle = createSelector(
  selectCurrentReport,
  (currentReport) => currentReport && currentReport.title
);

export const selectCurrentReportDescription = createSelector(
  selectCurrentReport,
  (currentReport) => currentReport && currentReport.description
);

/**
 * The entity family a COMBINED report reads across, or `undefined` for an ordinary one.
 *
 * Normalized from the record's `null` to `undefined` because every consumer is a request payload
 * field typed `entityGroupId?: string` — `null` would serialize onto the wire as an explicit
 * value, where absent is what an ordinary report means.
 */
export const selectCurrentReportEntityGroupId = createSelector(
  selectCurrentReport,
  (currentReport) => currentReport?.entityGroupId ?? undefined
);

export const selectIsReportDimensional = createSelector(selectCurrentReport, (report) =>
  Boolean(report && report.isDimensional)
);

export const selectIsReportMultiDataset = createSelector(selectCurrentReport, (report) =>
  Boolean(report && report.isMultiDataset)
);

export const selectIsReportMultiHierarchy = createSelector(selectCurrentReportConfiguration, (reportConfiguration) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!reportConfiguration) return false;
  const { accountSummary } = reportConfiguration;
  return !!accountSummary && isMultiHierarchyAccountSummary(accountSummary);
});

export const selectPublicReportExplorerUrl = createSelector(
  selectCurrentReport,
  selectCurrentReportConfiguration,
  selectCurrentSavedView,
  selectSavedViews,
  getEntitySubdomain,
  selectIsReportMultiHierarchy,
  (report, reportConfiguration, selectedSavedView, savedViews, entitySubdomain, isMultiHierarchy) => {
    if (!report) {
      return;
    }
    const browserOrigin = window?.location?.origin;
    const origin = isMultiHierarchy
      ? resolveTransparencyPublicApiHost({
          platformApiHost: ReportingConfig.PLATFORM_API_HOST,
          publicReportsApiHost: ReportingConfig.PUBLIC_REPORTS_API_HOST || undefined,
          usePublicReportsHost: true
        })
      : browserOrigin;
    if (!origin) {
      return;
    }
    let reportConfigurationId;
    let savedView;
    if (reportConfiguration && reportConfiguration.id) {
      reportConfigurationId = reportConfiguration.id;
    } else if (selectedSavedView) {
      reportConfigurationId = selectedSavedView.reportConfiguration;
    } else if (savedViews.length) {
      reportConfigurationId = savedViews[0].reportConfiguration;
    } else {
      reportConfigurationId = report.defaultReportConfiguration;
    }
    if (selectedSavedView && selectedSavedView.id) {
      savedView = selectedSavedView;
    } else if (savedViews.length) {
      savedView = savedViews[0];
    }
    // A saved view pins its own configuration, so the id stays in the link. A plain report link must
    // NOT pin one (BUD-18770): every save writes a new report configuration and repoints the report's
    // default at it, so a pinned id freezes public viewers on the snapshot that happened to be loaded
    // when the link was generated — expansion state and publicDrillCapabilities included. The
    // config-less transparency route resolves `defaultReportConfiguration` on each load instead.
    return savedView
      ? `${origin}${makeReportSavedViewPath({ isTransparency: true })(
          entitySubdomain,
          reportConfigurationId,
          savedView
        )}`
      : `${origin}${makeReportExplorerPath({ isTransparency: true })(entitySubdomain, report.id)}`;
  }
);

export const selectReportCreators = createSelector(reportSelector, (state) => state.reportCreators);

export const selectMuiToast = createSelector(reportSelector, (state) => state.get('muiToast').props);
